import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle, AlertCircle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [resetLink, setResetLink] = useState('');
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      
      // Check for error status (email configured but failed to send)
      if (response.data.error || response.data.email_status === 'error') {
        toast.error(response.data.message || 'Failed to send password reset email. Please try again later.');
        // Don't set sent=true - allow user to retry
        return;
      }
      
      setSent(true);
      
      if (response.data.email_sent) {
        setEmailConfigured(true);
        toast.success('Password reset email sent! Check your inbox.');
      } else if (response.data.reset_link) {
        // Email not configured - show manual link for testing
        setEmailConfigured(false);
        setResetLink(response.data.reset_link);
        toast.info('Email not configured. Use the link below to reset your password.');
      } else {
        toast.success('If the email exists, a reset link will be sent.');
      }
    } catch (error) {
      toast.error('Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-2">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
            {sent ? 'Check Your Email' : 'Forgot Password'}
          </CardTitle>
          <CardDescription>
            {sent 
              ? 'We\'ve sent you instructions to reset your password' 
              : 'Enter your email to receive a password reset link'
            }
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!sent ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="reset-email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="request-reset-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              {emailConfigured ? (
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="font-medium text-green-400">Email Sent!</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Check your inbox for a link to reset your password. 
                    The link will expire in 1 hour.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium text-yellow-400">Email Not Configured</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      The system admin hasn't configured email yet. Use the link below to reset your password.
                    </p>
                  </div>
                  
                  {resetLink && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Password Reset Link</span>
                      </div>
                      <a 
                        href={resetLink}
                        className="text-sm text-primary hover:underline break-all"
                        data-testid="manual-reset-link"
                      >
                        {resetLink}
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => window.location.href = resetLink}
                      >
                        Open Reset Link
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  Didn't receive the email? Check your spam folder.
                </p>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setSent(false);
                    setResetLink('');
                  }}
                >
                  Try a different email
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/login')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
